import { Badge, Button, Dropdown, Empty, List, Typography } from 'antd';
import { BellOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { shellRoute, type Shell } from '@/lib/shells';
import { useInbox, useMarkAllRead, useMarkRead, type Notification } from './api';

/**
 * The bell in the header (FR-NTF-01).
 *
 * Deliberately shallow: the five most recent, and a way to see the rest. A
 * dropdown that tried to be the whole inbox would be a list nobody can scroll on
 * a laptop and a second place to maintain the same rendering.
 */
export function NotificationBell({ shell }: { shell: Shell }) {
  const navigate = useNavigate();
  const { data } = useInbox();
  const markRead = useMarkRead();
  const markAllRead = useMarkAllRead();

  const items = data?.items ?? [];
  const unread = data?.unread ?? 0;
  const recent = items.slice(0, 5);

  const open = (notification: Notification) => {
    if (!notification.read_at) markRead.mutate(notification.id);
    if (notification.link_path) navigate(shellRoute(shell, notification.link_path));
  };

  const panel = (
    <div
      style={{
        width: 360,
        background: '#fff',
        borderRadius: 8,
        boxShadow: '0 6px 24px rgba(15, 23, 42, .18)',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '10px 14px',
          borderBottom: '1px solid #f1f5f9',
        }}
      >
        <Typography.Text strong>Notifications</Typography.Text>
        {unread > 0 && (
          <Button size="small" type="link" onClick={() => markAllRead.mutate()}>
            Mark all read
          </Button>
        )}
      </div>

      {recent.length === 0 ? (
        <div style={{ padding: 24 }}>
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Nothing yet." />
        </div>
      ) : (
        <List
          size="small"
          dataSource={recent}
          renderItem={(n) => (
            <List.Item
              onClick={() => open(n)}
              style={{
                cursor: 'pointer',
                paddingInline: 14,
                background: n.read_at ? undefined : '#f8fafc',
              }}
            >
              <List.Item.Meta
                title={
                  <Typography.Text strong={!n.read_at} style={{ fontSize: 13 }}>
                    {n.subject}
                  </Typography.Text>
                }
                description={
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {dayjs(n.created_at).format('D MMM, HH:mm')}
                  </Typography.Text>
                }
              />
            </List.Item>
          )}
        />
      )}

      <div style={{ borderTop: '1px solid #f1f5f9', padding: '8px 14px' }}>
        <Button type="link" size="small" onClick={() => navigate(shellRoute(shell, '/notifications'))}>
          See all
        </Button>
      </div>
    </div>
  );

  return (
    <Dropdown dropdownRender={() => panel} trigger={['click']} placement="bottomRight">
      {/* count={unread} rather than a dot: "you have things" is not as useful as
          how many, and an exact number is what stops somebody opening it out of
          uncertainty. */}
      <Badge count={unread} size="small" offset={[-2, 4]}>
        <Button
          type="text"
          icon={<BellOutlined style={{ fontSize: 18, color: '#e2e8f0' }} />}
          aria-label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'}
        />
      </Badge>
    </Dropdown>
  );
}
